/* eslint-disable max-params, no-param-reassign -- these linting errors come up constantly with reducers */
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { RootState } from "@/store";
import { useAppDispatch } from "@/store/hooks";
import { extraReducersFactory } from "@/store/reducers/reset";
import { isWeb } from "@/utils/platform.util";

interface SMTSliceState {
  addSMTEntrySelectedPatientId?: string;
  isFilePickerModalOpen: boolean;
  isSelectPatientModalOpen: boolean;
  photo?: FilePickerAsset | null;
  // TODO: Extend state in https://teamequip.atlassian.net/browse/JN-1486
}

const initialState: SMTSliceState = {
  addSMTEntrySelectedPatientId: undefined,
  isFilePickerModalOpen: false,
  isSelectPatientModalOpen: false,
  photo: undefined,
};

const smtSlice = createSlice({
  // NOTE: extraReducers is required for resetAllReduxSlices to work as expected
  extraReducers: extraReducersFactory(initialState),
  initialState,
  name: "smt",
  reducers: {
    resetSMTState: () => initialState,
    setAddSMTEntrySelectedPatientId: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.addSMTEntrySelectedPatientId = action.payload;
    },
    setIsFilePickerModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isFilePickerModalOpen = action.payload;
    },
    setIsSelectPatientModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isSelectPatientModalOpen = action.payload;
    },
    setPhoto: (
      state,
      action: PayloadAction<FilePickerAsset | null | undefined>,
    ) => {
      state.photo = action.payload;
    },
  },
});

const {
  resetSMTState: resetSMTStateAction,
  setAddSMTEntrySelectedPatientId: setAddSMTEntrySelectedPatientIdAction,
  setIsFilePickerModalOpen: setIsFilePickerModalOpenAction,
  setIsSelectPatientModalOpen: setIsSelectPatientModalOpenAction,
  setPhoto: setPhotoAction,
} = smtSlice.actions;

// Selectors
const selectIsSMTFilePickerModalOpen = (state: RootState) =>
  state.smt.isFilePickerModalOpen;
const selectIsSMTPatientSelectorModalOpen = (state: RootState) =>
  state.smt.isSelectPatientModalOpen;
const selectSMTPhoto = (state: RootState) => state.smt.photo;

const selectAddSMTEntrySelectedPatientId = (state: RootState) =>
  state.smt.addSMTEntrySelectedPatientId;

// Custom hook for dispatch actions
const useSMTDispatch = () => {
  const dispatch = useAppDispatch();

  const setIsFilePickerModalOpen = (isOpen: boolean) => {
    dispatch(setIsFilePickerModalOpenAction(isOpen));
  };

  const setIsSelectPatientModalOpen = (isOpen: boolean) => {
    dispatch(setIsSelectPatientModalOpenAction(isOpen));
  };

  const setPhoto = (photo?: FilePickerAsset | null) => {
    // on web, the picked asset has a non-serializable DOM `File` in photo.file, that needs to be dropped
    const filelessPhoto = photo ? { ...photo, file: undefined } : photo;
    const serializablePhoto = isWeb() ? filelessPhoto : photo;
    dispatch(setPhotoAction(serializablePhoto));
  };

  const setAddSMTEntrySelectedPatientId = (patientId?: string) => {
    dispatch(setAddSMTEntrySelectedPatientIdAction(patientId));
  };

  const resetSMTState = () => {
    dispatch(resetSMTStateAction());
  };

  return {
    resetSMTState,
    setAddSMTEntrySelectedPatientId,
    setIsFilePickerModalOpen,
    setIsSelectPatientModalOpen,
    setPhoto,
  };
};

export type { SMTSliceState };

export {
  resetSMTStateAction,
  selectAddSMTEntrySelectedPatientId,
  selectIsSMTFilePickerModalOpen,
  selectIsSMTPatientSelectorModalOpen,
  selectSMTPhoto,
  setAddSMTEntrySelectedPatientIdAction,
  setIsFilePickerModalOpenAction,
  setIsSelectPatientModalOpenAction,
  setPhotoAction,
  useSMTDispatch,
};
export default smtSlice.reducer;
